/**
 * /litters/upcoming/new — Breeder creates a planned-breeding or expected
 * litter draft. After save, redirects to the listing's edit/manage page
 * where the breeder pays the listing fee to publish.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DogPicker from "@/components/DogPicker";
import DateOfBirthInput from "@/components/DateOfBirthInput";
import { formatApiError } from "@/lib/utils-app";

export default function UpcomingLitterNew() {
  const nav = useNavigate();
  const [breeders, setBreeders] = useState([]);
  const [breederId, setBreederId] = useState("");
  const [sire, setSire] = useState(null);
  const [sireExternalName, setSireExternalName] = useState("");
  const [sireExternalPhoto, setSireExternalPhoto] = useState("");
  const [dam, setDam] = useState(null);
  const [form, setForm] = useState({
    expected_breeding_date: "",
    expected_whelp_date: "",
    estimated_puppy_count: 6,
    price_per_puppy: "3000",
    deposit_amount: "500",
    health_summary: "",
    refund_policy_text:
      "Deposits are non-refundable except in the event the breeder cancels the litter, the dam fails to conceive, or no puppy of the buyer's requested sex is available. In those cases the deposit is fully refunded within 14 days.",
    pickup_options: "Pickup at kennel; transport via Armstrong Transport",
    allow_transport: true,
    additional_info: "",
    video_call_offer_price: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/breeders");
        setBreeders(r.data || []);
        if ((r.data || []).length === 1) setBreederId(r.data[0].id);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!breederId) return toast.error("Pick the kennel this litter belongs to.");
    if (!dam) return toast.error("Pick the dam from your registered dogs.");
    if (!sire && !sireExternalName.trim()) {
      return toast.error("Pick a registered sire OR enter the external sire's name.");
    }
    if (!form.refund_policy_text.trim()) {
      return toast.error("Refund policy is required.");
    }
    setSaving(true);
    try {
      const payload = {
        breeder_id: breederId,
        sire_id: sire?.id,
        sire_external: !sire
          ? { name: sireExternalName.trim(), photo_url: sireExternalPhoto.trim() || null }
          : null,
        dam_id: dam.id,
        expected_breeding_date: form.expected_breeding_date || null,
        expected_whelp_date: form.expected_whelp_date || null,
        estimated_puppy_count: Number(form.estimated_puppy_count) || null,
        price_per_puppy_cents: Math.round(Number(form.price_per_puppy || 0) * 100),
        deposit_amount_cents: Math.round(Number(form.deposit_amount || 0) * 100),
        health_summary: form.health_summary,
        refund_policy_text: form.refund_policy_text,
        pickup_options: form.pickup_options
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean),
        allow_transport: form.allow_transport,
        additional_info: form.additional_info,
        video_call_offer_price_cents: form.video_call_offer_price
          ? Math.round(Number(form.video_call_offer_price) * 100)
          : null,
      };
      const r = await api.post("/upcoming-breedings", payload);
      toast.success("Draft saved — review and publish from the next screen.");
      nav(`/litters/upcoming/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't save the draft."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">
            Upcoming Litter
          </p>
          <h1 className="font-spectral text-4xl tracking-tight">
            List a planned breeding or expected litter
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-2">
            Save the draft first, then pay the listing fee to publish — $25 for a
            planned breeding, $40 once you've confirmed the breeding happened.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {breeders.length > 1 && (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Kennel
              </Label>
              <select
                value={breederId}
                onChange={(e) => setBreederId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E5E5E5] p-3 bg-white"
                data-testid="upcoming-breeder-select"
              >
                <option value="">— pick —</option>
                {breeders.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.kennel_name || b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Dam (your registered female)
              </Label>
              <div className="mt-1">
                <DogPicker
                  value={dam?.id}
                  onChange={(id, obj) => setDam(obj)}
                  sexFilter="female"
                  placeholder="Search dams"
                  testId="upcoming-dam-picker"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Sire (registered)
              </Label>
              <div className="mt-1">
                <DogPicker
                  value={sire?.id}
                  onChange={(id, obj) => setSire(obj)}
                  sexFilter="male"
                  placeholder="Search sires"
                  testId="upcoming-sire-picker"
                />
              </div>
              {!sire && (
                <div className="mt-2 space-y-2">
                  <Input
                    placeholder="…or external sire name"
                    value={sireExternalName}
                    onChange={(e) => setSireExternalName(e.target.value)}
                    data-testid="upcoming-sire-external-name"
                  />
                  <Input
                    placeholder="External sire photo URL (optional)"
                    value={sireExternalPhoto}
                    onChange={(e) => setSireExternalPhoto(e.target.value)}
                    data-testid="upcoming-sire-external-photo"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Expected breeding date
              </Label>
              <DateOfBirthInput
                value={form.expected_breeding_date}
                onChange={(v) => set("expected_breeding_date", v)}
                autoComplete="off"
                ariaLabel="Expected breeding date"
                testId="upcoming-breeding-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Expected whelp date
              </Label>
              <DateOfBirthInput
                value={form.expected_whelp_date}
                onChange={(v) => set("expected_whelp_date", v)}
                autoComplete="off"
                ariaLabel="Expected whelp date"
                testId="upcoming-whelp-date"
              />
              <p className="text-[11px] text-[#6B6B6B] mt-1">
                Earliest go-home date is auto-set to whelp + 56 days (8-week rule).
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Est. puppies
              </Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={form.estimated_puppy_count}
                onChange={(e) => set("estimated_puppy_count", e.target.value)}
                data-testid="upcoming-puppy-count"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Price per puppy (USD)
              </Label>
              <Input
                type="number"
                step="50"
                value={form.price_per_puppy}
                onChange={(e) => set("price_per_puppy", e.target.value)}
                data-testid="upcoming-price"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Deposit (USD)
              </Label>
              <Input
                type="number"
                step="25"
                value={form.deposit_amount}
                onChange={(e) => set("deposit_amount", e.target.value)}
                data-testid="upcoming-deposit"
                className="mt-1"
              />
              <p className="text-[11px] text-[#6B6B6B] mt-1">
                Armstrong fee: 5% (max $25). Net flows to you.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
              Health testing & disclosures
            </Label>
            <Textarea
              rows={3}
              value={form.health_summary}
              onChange={(e) => set("health_summary", e.target.value)}
              placeholder="OFA hips/elbows, cardiac, DNA panels, any known conditions…"
              data-testid="upcoming-health"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
              Refund policy (your text — reviewed by your lawyer)
            </Label>
            <Textarea
              rows={4}
              value={form.refund_policy_text}
              onChange={(e) => set("refund_policy_text", e.target.value)}
              data-testid="upcoming-refund-policy"
              className="mt-1"
              required
            />
            <p className="text-[11px] text-[#6B6B6B] mt-1">
              This is YOUR policy. Buyers must acknowledge it before paying a deposit.
              Armstrong is not a party to it.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
              Pickup options (semicolon separated)
            </Label>
            <Input
              value={form.pickup_options}
              onChange={(e) => set("pickup_options", e.target.value)}
              data-testid="upcoming-pickup"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
              Anything else (optional)
            </Label>
            <Textarea
              rows={3}
              value={form.additional_info}
              onChange={(e) => set("additional_info", e.target.value)}
              placeholder="Lifetime support, photo update cadence, any extras…"
              data-testid="upcoming-additional"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
              Live video call price (optional)
            </Label>
            <Input
              type="number"
              step="5"
              value={form.video_call_offer_price}
              onChange={(e) => set("video_call_offer_price", e.target.value)}
              placeholder="e.g. 35 for a 15-min kennel walkthrough"
              data-testid="upcoming-video-price"
              className="mt-1"
            />
            <p className="text-[11px] text-[#6B6B6B] mt-1">
              If set, prospective buyers can book a paid video call. Same fee model
              (you receive net, Armstrong takes 5% capped at $25).
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => nav("/litters")}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-ink hover:bg-crimson text-white rounded-full"
              data-testid="upcoming-save-draft"
            >
              {saving ? "Saving…" : "Save draft & continue"}
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
